import { apiFetch } from '../../utils/apiClient';
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// ─── DATA FROM AEGIS TECHNICAL REPORT + SOC EVALUATION REPORT ──────────────

const MODELS_INFO = [
  {
    id: 1, folder: 'Production',
    name: 'AEGIS Hybrid v2.1',
    desc: 'AE + LightGBM · Full Phase 2 stream · SOC Evaluation Report',
    acc: null, f1: 82.78, auc: 99.9988, mcc: 83.02,
    precision: 76.97, recall: 89.54,
    fpr: 0.0028, fnr: 10.46,
    tp: 899, fp: 269, fn: 105, tn: 9535550,
    dataset: 'CICAPT-IIoT Ph2 (full 9.5M flows)',
    threshold: '0.9970',
    trees: 137,
    features: 62,
    badge: 'PRODUCTION',
    badgeColor: '#10b981',
  },
  {
    id: 2, folder: 'Test',
    name: 'Held-Out Test Set',
    desc: 'Threshold=0.9970 · 1,820,000 flows · no-ports retrain',
    acc: null, f1: 84.47, auc: 99.99, mcc: 84.53,
    precision: 81.31, recall: 87.88,
    fpr: 0.0022, fnr: 12.12,
    tp: 174, fp: 40, fn: 24, tn: 1819762,
    dataset: 'CICAPT-IIoT Ph2 (20% test split)',
    threshold: '0.9970',
    trees: 137,
    features: 62,
    badge: 'EVALUATED',
    badgeColor: '#3b82f6',
  },
  {
    id: 3, folder: 'Val',
    name: 'Validation Set',
    desc: 'Threshold tuning · 1,907,365 flows · best MCC on val',
    acc: null, f1: 80.37, auc: 99.99, mcc: 80.57,
    precision: 75.0, recall: 86.57,
    fpr: 0.003, fnr: 13.43,
    tp: 174, fp: 58, fn: 27, tn: 1907106,
    dataset: 'CICAPT-IIoT Ph2 (20% val split)',
    threshold: '0.9970',
    trees: 137,
    features: 62,
    badge: 'TUNING',
    badgeColor: '#f59e0b',
  },
];

// Feature importance from SHAP analysis (Chapter 7, Table 7.4) — CICAPT-IIoT
// native packet-level feature schema (Chapter 6, Section 6.1.2), NOT CICFlowMeter.
const FEATURE_IMPORTANCE = [
  { feature: 'syn_count',     importance: 0.530465, share: 15.96 },
  { feature: 'max_duration',  importance: 0.410592, share: 12.36 },
  { feature: 'Rate',          importance: 0.331558, share:  9.98 },
  { feature: 'sum_duration',  importance: 0.273347, share:  8.23 },
  { feature: 'Header_Length', importance: 0.219591, share:  6.61 },
  { feature: 'rst_count',     importance: 0.181780, share:  5.47 },
  { feature: 'min_duration',  importance: 0.136568, share:  4.11 },
  { feature: 'Tot size',      importance: 0.134429, share:  4.05 },
  { feature: 'urg_count',     importance: 0.116106, share:  3.49 },
  { feature: 'std_duration',  importance: 0.114944, share:  3.46 },
  { feature: 'Srate',         importance: 0.109986, share:  3.31 },
  { feature: 'Magnitue',      importance: 0.100141, share:  3.01 },
];

// State-of-the-art comparison — Chapter 7, Table 7.3 (independently verified citations only).
const SOTA_COMPARISON = [
  { name: 'AEGIS SOC (This Work)',              recall: 89.54, fpr: 0.0028, f1: 82.78, color: '#3b82f6', highlight: true },
  { name: 'Kitsune (Mirsky et al. 2018)',       recall: 85.0, fpr: 0.10,   f1: null, color: '#8b5cf6', highlight: false },
  { name: 'DeepLog (Du et al. 2017)',           recall: 95.7, fpr: 0.10,   f1: null, color: '#f59e0b', highlight: false },
  { name: 'FlowTransformer (Manocchio et al. 2024)', recall: 97.2, fpr: 0.28, f1: 97.1, color: '#ef4444', highlight: false },
  { name: 'ET-BERT (Lin et al. 2022)',          recall: null, fpr: null,   f1: 99.2, color: '#6b7280', highlight: false },
];

// Alert severity from SOC Eval §3.3
const ALERT_SEVERITY = [
  { level: 'CRITICAL', count: 636, pct: 55.7, color: '#ef4444' },
  { level: 'HIGH',     count: 299, pct: 26.2, color: '#f97316' },
  { level: 'MEDIUM',   count: 189, pct: 16.5, color: '#f59e0b' },
  { level: 'LOW',      count: 18,  pct: 1.6,  color: '#6b7280' },
];

// Top attack types from SOC Eval §3.4
const ATTACK_TYPES = [
  { type: 'APT-Data-Exfiltration',   count: 558, pct: 48.9, severity: 'CRITICAL', color: '#ef4444' },
  { type: 'APT-Defense-Evasion',     count: 179, pct: 15.7, severity: 'HIGH',     color: '#f97316' },
  { type: 'APT-Reconnaissance',      count: 159, pct: 13.9, severity: 'MEDIUM',   color: '#f59e0b' },
  { type: 'APT-Privilege-Escalation',count: 93,  pct: 8.1,  severity: 'HIGH',     color: '#f97316' },
  { type: 'APT-C2-Communication',    count: 78,  pct: 6.8,  severity: 'CRITICAL', color: '#ef4444' },
  { type: 'APT-Persistence',         count: 30,  pct: 2.6,  severity: 'MEDIUM',   color: '#f59e0b' },
];

// ─── COMPONENTS ──────────────────────────────────────────────────────────

function Panel({ children, style }) {
  return (
    <div style={{
      background: '#141720',
      border: '1px solid #ffffff14',
      borderRadius: 8,
      padding: '14px 16px',
      ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 11,
      color: '#9ca3af',
      fontFamily: 'JetBrains Mono, monospace',
      letterSpacing: '.1em',
      marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function MetricCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: '#1a1e2a',
      border: '1px solid #ffffff14',
      borderRadius: 6,
      padding: '10px 12px',
      textAlign: 'center',
    }}>
      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 600, color }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, letterSpacing: '.05em' }}>{label}</div>
      {sub && <div style={{ fontSize: 9, color: '#4b5563', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────

export default function ModelPanel() {
  const [activeModel, setActiveModel] = useState(1);
  const [switching, setSwitching] = useState(false);
  const [liveMetrics, setLiveMetrics] = useState(null);

  useEffect(() => {
    fetch('http://localhost:8000/api/metrics')
      .then(r => r.json())
      .then(d => setLiveMetrics(d))
      .catch(() => {});
  }, []);

  async function switchModel(num) {
    if (num === activeModel) return;
    setActiveModel(num);
    setSwitching(true);
    try {
      await apiFetch(`/model/switch/${num}`, { method: 'POST' });
    } catch (e) {
      console.error('Switch failed:', e);
    }
    setSwitching(false);
  }

  const mod = MODELS_INFO.find(m => m.id === activeModel) || MODELS_INFO[0];

  const fmt = (v, suffix = '%') => v != null ? `${v}${suffix}` : '—';
  const fmtN = (v) => v != null ? v.toLocaleString() : '—';

  const perf = [
    { label: 'RECALL (TPR)',  value: fmt(mod.recall),    color: '#10b981', sub: 'Attack detection rate' },
    { label: 'PRECISION',     value: fmt(mod.precision), color: '#f59e0b', sub: 'Alert accuracy'        },
    { label: 'F1 SCORE',      value: fmt(mod.f1),        color: '#3b82f6', sub: 'Harmonic mean'         },
    { label: 'AUC-ROC',       value: fmt(mod.auc),       color: '#06b6d4', sub: 'ROC area'              },
    { label: 'MCC',           value: fmt(mod.mcc),       color: '#8b5cf6', sub: 'Balanced metric'       },
    { label: 'FPR',           value: fmt(mod.fpr),       color: '#f43f5e', sub: 'False alarm rate'      },
  ];

  return (
    <div style={{ display: 'grid', gap: 16, fontFamily: 'system-ui, sans-serif', color: '#e8eaf0' }}>

      {/* ── Dataset + Pipeline Summary ───────────────────────────────── */}
      <Panel style={{ background: '#0d111a', borderColor: '#3b82f644' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#e8eaf0', fontFamily: 'JetBrains Mono, monospace' }}>AEGIS SOC — APT Detection System</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Hybrid Autoencoder + LightGBM · CICAPT-IIoT Phase 2 · Researcher: Ramanaruban · 2026</div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              ['9,536,823', 'Total Flows', '#3b82f6'],
              ['9,498:1',   'Imbalance',   '#f59e0b'],
              ['62',   'Features',    '#8b5cf6'],
            ].map(([v, l, c]) => (
              <div key={l} style={{ textAlign: 'center', background: '#141720', border: '1px solid #ffffff14', borderRadius: 6, padding: '8px 14px' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: c, fontFamily: 'JetBrains Mono, monospace' }}>{v}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      {/* ── Model Selector ───────────────────────────────────────────── */}
      <Panel>
        <SectionTitle>MODEL / EVALUATION SELECTOR</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {MODELS_INFO.map(m => (
            <div
              key={m.id}
              onClick={() => !switching && switchModel(m.id)}
              style={{
                background: activeModel === m.id ? '#1e3a5f' : '#1a1e2a',
                border: `1px solid ${activeModel === m.id ? '#3b82f6' : '#ffffff14'}`,
                borderRadius: 7,
                padding: '12px 14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              <span style={{
                position: 'absolute', top: 8, right: 8,
                fontSize: 8,
                background: m.badgeColor,
                color: 'white',
                padding: '1px 6px',
                borderRadius: 3,
                fontFamily: 'JetBrains Mono, monospace',
              }}>
                {m.badge}
              </span>
              <div style={{ fontSize: 10, color: '#6b7280', fontFamily: 'JetBrains Mono, monospace', marginBottom: 4 }}>
                {m.folder}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e8eaf0', fontFamily: 'JetBrains Mono, monospace', marginBottom: 6 }}>
                {m.name}
              </div>
              <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 10 }}>{m.desc}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {[
                  ['TPR',  m.recall != null   ? `${m.recall}%`    : '—', '#10b981'],
                  ['F1',   m.f1 != null        ? `${m.f1}%`        : '—', '#3b82f6'],
                  ['FPR',  m.fpr != null       ? `${m.fpr}%`       : '—', '#f43f5e'],
                  ['MCC',  m.mcc != null       ? `${m.mcc}%`       : '—', '#8b5cf6'],
                ].map(([k, v, c]) => (
                  <div key={k} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: c, fontFamily: 'JetBrains Mono, monospace' }}>{v}</div>
                    <div style={{ fontSize: 9, color: '#6b7280' }}>{k}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* ── Performance KPIs ────────────────────────────────────────── */}
      <Panel>
        <SectionTitle>
          MODEL PERFORMANCE — {mod.name.toUpperCase()} · DATASET: {mod.dataset} · THRESHOLD: {mod.threshold}
        </SectionTitle>

        {/* Primary KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 10, marginBottom: 16 }}>
          {perf.map(p => <MetricCard key={p.label} {...p} />)}
        </div>

        {/* FPR / FNR highlight row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 16 }}>
          {[
            {
              label: 'FALSE POSITIVE RATE',
              value: fmt(mod.fpr),
              color: mod.fpr != null && mod.fpr < 0.01 ? '#10b981' : '#f59e0b',
              note: mod.id === 1
                ? `${fmtN(mod.fp)} false alarms across 9.5M flows  35× better than CIC baseline`
                : 'Normal flows incorrectly flagged as attack',
            },
            {
              label: 'FALSE NEGATIVE RATE',
              value: fmt(mod.fnr),
              color: mod.fnr != null && mod.fnr < 15 ? '#10b981' : '#f59e0b',
              note: mod.id === 1
                ? 'Missed attacks mainly Persistence-phase (syn_count=0) — dataset ceiling, not model failure'
                : 'Attacks missed by classifier',
            },
          ].map(p => (
            <div key={p.label} style={{
              background: '#1a1e2a',
              border: '1px solid #ffffff14',
              borderRadius: 6,
              padding: '10px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#6b7280', letterSpacing: '.05em', marginBottom: 2 }}>{p.label}</div>
                <div style={{ fontSize: 10, color: '#4b5563', lineHeight: 1.4 }}>{p.note}</div>
              </div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 600, color: p.color, whiteSpace: 'nowrap' }}>
                {p.value}
              </div>
            </div>
          ))}
        </div>

        {/* Confusion Matrix + SOTA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Confusion Matrix */}
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontFamily: 'JetBrains Mono, monospace' }}>
              Confusion Matrix — {mod.dataset}
            </div>
            {mod.tp != null ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, maxWidth: 280 }}>
                {[
                  { v: fmtN(mod.tn), lbl: 'True Negative',  c: '#10b981' },
                  { v: fmtN(mod.fp), lbl: 'False Positive', c: '#ff3b4e' },
                  { v: fmtN(mod.fn), lbl: 'False Negative', c: '#f59e0b' },
                  { v: fmtN(mod.tp), lbl: 'True Positive',  c: '#3b82f6' },
                ].map(item => (
                  <div key={item.lbl} style={{
                    background: `${item.c}22`,
                    border: `1px solid ${item.c}44`,
                    padding: '10px 8px',
                    borderRadius: 5,
                    textAlign: 'center',
                  }}>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 600, color: item.c }}>{item.v}</div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{item.lbl}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 12, padding: 12 }}>Confusion matrix not published for this reference result.</div>
            )}
            <div style={{ marginTop: 10, padding: '8px 10px', background: '#1a1e2a', borderRadius: 6, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#6b7280', lineHeight: 1.9 }}>
              <div><span style={{ color: '#9ca3af' }}>Dataset:</span> {mod.dataset}</div>
              <div><span style={{ color: '#9ca3af' }}>Pipeline:</span> Autoencoder (61→8→61) → LightGBM</div>
              <div><span style={{ color: '#9ca3af' }}>Features:</span> {mod.features != null ? `${mod.features} (anomaly_score + 61 raw)` : '—'}</div>
              <div><span style={{ color: '#9ca3af' }}>Threshold:</span> {mod.threshold}</div>
              {mod.trees && <div><span style={{ color: '#9ca3af' }}>Trees (best iter):</span> {mod.trees}</div>}
            </div>
          </div>

          {/* SOTA Comparison */}
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontFamily: 'JetBrains Mono, monospace' }}>
              State-of-the-Art Comparison (FPR — lower is better)
            </div>
            {SOTA_COMPARISON.map(s => (
              <div key={s.name} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontFamily: 'JetBrains Mono, monospace', marginBottom: 3 }}>
                  <span style={{ color: s.highlight ? '#e8eaf0' : '#9ca3af' }}>{s.name}</span>
                  <span style={{ color: s.color }}>
                    {s.fpr != null ? `FPR ${s.fpr}%` : 'FPR —'}  {s.recall != null ? `TPR ${s.recall}%` : (s.f1 != null ? `F1 ${s.f1}%` : 'TPR —')}
                  </span>
                </div>
                {/* FPR bar (log-scale approximation for readability) */}
                <div style={{ height: 5, background: '#ffffff14', borderRadius: 3 }}>
                  <div style={{
                    width: `${Math.min(100, ((s.fpr ?? 0) / 2.2) * 100)}%`,
                    height: '100%',
                    background: s.color,
                    borderRadius: 3,
                    minWidth: s.fpr != null && s.fpr < 0.01 ? 3 : undefined,
                  }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 8, padding: '6px 10px', background: '#10b98122', borderRadius: 5, border: '1px solid #10b98133', fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#9ca3af', lineHeight: 1.7 }}>
              <span style={{ color: '#10b981' }}>AEGIS FPR 0.0028%</span> — cross-dataset comparison is approximate (Chapter 2, Section 2.1)
            </div>
          </div>
        </div>
      </Panel>

      {/* ── Alerts + Attack Types (Production model only) ─────────────── */}
      {activeModel === 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Panel>
            <SectionTitle>ALERT SEVERITY DISTRIBUTION — 1,142 CONFIRMED ALERTS</SectionTitle>
            {ALERT_SEVERITY.map(a => (
              <div key={a.level} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', marginBottom: 3 }}>
                  <span style={{ color: a.color }}>{a.level}</span>
                  <span style={{ color: '#9ca3af' }}>{a.count.toLocaleString()} ({a.pct}%)</span>
                </div>
                <div style={{ height: 7, background: '#ffffff14', borderRadius: 3 }}>
                  <div style={{ width: `${a.pct}%`, height: '100%', background: a.color, borderRadius: 3 }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 10, padding: '8px 10px', background: '#1a1e2a', borderRadius: 6, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#6b7280', lineHeight: 1.8 }}>
              <div><span style={{ color: '#9ca3af' }}>Total Alerts:</span> 1,142</div>
              <div><span style={{ color: '#9ca3af' }}>Correlated Incidents:</span> 216 (~5.3 alerts/incident)</div>
              <div><span style={{ color: '#9ca3af' }}>Two-Tier:</span> AUTO-ALERT (Tier 1) + REVIEW-QUEUE (Tier 2)</div>
            </div>
          </Panel>

          <Panel>
            <SectionTitle>TOP ATTACK TYPES DETECTED</SectionTitle>
            {ATTACK_TYPES.map(a => (
              <div key={a.type} style={{ marginBottom: 9 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontFamily: 'JetBrains Mono, monospace', marginBottom: 3 }}>
                  <span style={{ color: '#e8eaf0' }}>{a.type}</span>
                  <span>
                    <span style={{ color: a.color, marginRight: 6 }}>{a.severity}</span>
                    <span style={{ color: '#6b7280' }}>{a.count} ({a.pct}%)</span>
                  </span>
                </div>
                <div style={{ height: 5, background: '#ffffff14', borderRadius: 3 }}>
                  <div style={{ width: `${a.pct}%`, height: '100%', background: a.color, borderRadius: 3 }} />
                </div>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* ── Feature Importance + Model Config ─────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Panel>
  <SectionTitle>TOP 12 FEATURES — MEAN |SHAP| VALUE</SectionTitle>
  <ResponsiveContainer width="100%" height={310}>
    <BarChart layout="vertical" data={FEATURE_IMPORTANCE} margin={{ left: 10, right: 20 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" horizontal={false} />
      <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} tickLine={false} />
      <YAxis
        type="category"
        dataKey="feature"
        tick={{ fill: '#9ca3af', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }}
        tickLine={false}
        width={155}
      />
      <Tooltip
        formatter={(v, name, props) => [`${v.toFixed(4)} (${props.payload.share}% of total)`, 'mean |SHAP|']}
        contentStyle={{
          background: '#1a1e2a',
          border: '1px solid #ffffff22',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'JetBrains Mono, monospace',
          color: '#e8eaf0',
        }}
      />
      <Bar dataKey="importance" fill="url(#featureGrad)" radius={[0, 3, 3, 0]}>
        <defs>
          <linearGradient id="featureGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
      </Bar>
    </BarChart>
  </ResponsiveContainer>
  <div style={{ marginTop: 8, padding: '6px 10px', background: '#1a1e2a', borderRadius: 6, fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#6b7280' }}>
    With ports eliminated (Ch. 6 ablation), syn_count leads by mean |SHAP| (15.96%), followed by behavioral flow features (max_duration, Rate, sum_duration) — confirming the model now relies on legitimate traffic behavior rather than port identity.
  </div>
</Panel>

        <Panel>
          <SectionTitle>MODEL CONFIGURATION — LIGHTGBM CLASSIFIER</SectionTitle>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, lineHeight: 2 }}>
            {[
              ['n_estimators (max)',    2000],
              ['best_iteration',       137],
              ['learning_rate',        0.05],
              ['max_depth',            8],
              ['num_leaves',           63],
              ['min_child_samples',    20],
              ['subsample',            0.8],
              ['colsample_bytree',     0.8],
              ['reg_alpha (L1)',       0.1],
              ['reg_lambda (L2)',      0.1],
              ['scale_pos_weight',     1],
              ['n_features',           62],
              ['train_rows',           '30,294 (50:1 undersample)'],
              ['objective',            'binary'],
              ['early_stop_metric',    'AUC'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #ffffff08', padding: '1px 0' }}>
                <span style={{ color: '#6b7280' }}>{k}</span>
                <span style={{ color: '#60a5fa' }}>{String(v)}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#10b98122', borderRadius: 6, border: '1px solid #10b98133' }}>
            <div style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#9ca3af', lineHeight: 1.9 }}>
              <div><span style={{ color: '#6b7280' }}>Stage 1 AE:</span> Dense 61→48→32→16→8→16→32→48→61</div>
              <div><span style={{ color: '#6b7280' }}>Separation ratio:</span> 2.49× (benign vs attack MSE)</div>
              <div><span style={{ color: '#6b7280' }}>Scaler:</span> RobustScaler (median + IQR) · clip 10</div>
              <div><span style={{ color: '#6b7280' }}>Score clip:</span> benign p99 threshold (ae_clip_meta.json)</div>
              <div><span style={{ color: '#6b7280' }}>Deploy threshold:</span> 0.9970 (best MCC on val)</div>
              <div><span style={{ color: '#6b7280' }}>Imbalance fix:</span> 50:1 random undersample (not scale_pos_weight)</div>
            </div>
          </div>
        </Panel>
      </div>

      {/* ─ Threshold Analysis ──────────────────────────────────────── */}
      <Panel>
        <SectionTitle>THRESHOLD ANALYSIS (VALIDATION SET — KEY ROWS FROM GRID SWEEP)</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ffffff14' }}>
                {['Threshold', 'TPR', 'FPR', 'F1', 'MCC', 'TP', 'FP', 'FN', 'Note'].map(h => (
                  <th key={h} style={{ padding: '6px 10px', color: '#6b7280', textAlign: 'left', fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['0.0010',   '100.00%', '0.4335%', '0.0478', '0.1561', 198, 7889, 0,  ''],
                ['0.2000',   '99.49%',  '0.0238%', '0.4758', '0.5577', 197, 433,  1,  ''],
                ['0.6000',   '98.99%',  '0.0173%', '0.5529', '0.6161', 196, 315,  2,  ''],
                ['0.8000',   '98.48%',  '0.0141%', '0.6009', '0.6525', 195, 256,  3,  ''],
                ['0.9000',   '97.47%',  '0.0120%', '0.6338', '0.6765', 193, 218,  5,  ''],
                ['0.9970 ★', '84.85%',  '0.0031%', '0.7943', '0.7959', 168, 57,   30, 'DEPLOYED'],
                ['0.9990',   '71.21%',  '0.0014%', '0.7747', '0.7777', 141, 25,   57, ''],
              ].map(([thresh, tpr, fpr, f1, mcc, tp, fp, fn, note]) => (
                <tr key={thresh} style={{
                  borderBottom: '1px solid #ffffff08',
                  background: thresh.includes('') ? '#1e3a5f44' : 'transparent',
                }}>
                  {[thresh, tpr, fpr, f1, mcc, tp, fp, fn, note].map((v, i) => (
                    <td key={i} style={{
                      padding: '5px 10px',
                      color: i === 0 && thresh.includes('★') ? '#3b82f6'
                           : i === 1 ? '#10b981'
                           : i === 2 ? '#f43f5e'
                           : i === 8 ? '#f59e0b'
                           : '#9ca3af',
                      fontWeight: thresh.includes('★') && i < 2 ? 600 : 400,
                    }}>
                      {typeof v === 'number' ? v.toLocaleString() : v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: '#6b7280' }}>
          ★ Deployed threshold selected by best MCC on validation set. Sweep against no-ports retrain (62 features), same 7 reference thresholds as prior grid.
        </div>
      </Panel>

    </div>
  );
}